import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { useContactSessions } from '@/hooks/useContactSessions';
import { Zap } from 'lucide-react';

interface EffortIndicatorProps {
  contactId: string | null;
}

export function EffortIndicator({ contactId }: EffortIndicatorProps) {
  const { data: sessions } = useContactSessions(contactId);

  if (!contactId || !sessions || sessions.length === 0) return null;

  // We don't have follow_up_count on the basic contact sessions query,
  // so we calculate a simplified effort score based on session count vs resolved
  const resolved = sessions.filter(s => s.status === 'resolved').length;
  const total = sessions.length;
  
  // For now, use total sessions / resolved as a proxy
  // The real follow_up_count would come from the full session data
  const score = resolved > 0 ? ((total - resolved) / resolved) : 0;
  const progressValue = Math.min((score / 5) * 100, 100);

  const getColor = (s: number) => {
    if (s < 0.5) return { text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500' };
    if (s < 2) return { text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500' };
    return { text: 'text-red-600 dark:text-red-400', bg: 'bg-red-500' };
  };

  const colors = getColor(score);

  return (
    <div className="p-3 rounded-lg bg-muted/50">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Índice de Esforço</span>
      </div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <span className={cn('text-2xl font-bold', colors.text)}>
          {score.toFixed(1)}
        </span>
        <span className="text-xs text-muted-foreground">cobranças / chamado (meta: &lt; 0.5)</span>
      </div>
      <Progress value={progressValue} className={cn('h-1.5', `[&>div]:${colors.bg}`)} />
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-muted-foreground">{total} chamados · {resolved} resolvidos</span>
      </div>
    </div>
  );
}
